import React, { useState, useRef, useEffect, useMemo, useCallback } from "react"
import { chevronDown } from "../hooks/icons"
import { useSearchspringContext } from "@app/scripts/providers/searchspring"
import RangeSlider from "react-range-slider-input"
import { currentCountryCurrency, sizeLabel } from "@app/scripts/lib/utils"

const SIZE_FACET_FIELD = "ss_filter_sizes"

// Canonical display order for merged size pills: one-size, alpha run,
// numerics ascending, then anything else in the order Searchspring returned.
const ALPHA_SIZE_ORDER = ["OS", "XXS", "XS", "S", "M", "L", "XL", "XXL"]

const sizeRank = (label: string) => {
  const alphaIndex = ALPHA_SIZE_ORDER.indexOf(label.toUpperCase())
  if (alphaIndex !== -1) return alphaIndex
  const numeric = Number(label)
  if (!Number.isNaN(numeric)) return 100 + numeric
  return 1000
}

type FilterValue = {
  value?: string
  label?: string
  count?: number
  field?: string
  active?: boolean
}

type FilterData = {
  field: string
  label: string
  values: FilterValue[]
  range?: [number, number]
  active?: [number, number]
  activeSort?: string
}

type Props = {
  data: FilterData
  handleToggleFilter: (value: string) => void
  activeFilters: Set<string>
}

export function FitlerSortCollapsibleRow({
  data,
  handleToggleFilter,
  activeFilters,
}: Props) {
  const [isOpen, setIsOpen] = useState(false)
  const detailsRef = useRef<HTMLDetailsElement | null>(null)

  const { refine } = useSearchspringContext()
  const isSortBy = data?.field === "sort_by"
  const isPrice = data?.field === "ss_price"
  const [stableRange, setStableRange] = useState<[number, number]>(
    data?.range || [0, 1000]
  )
  const [currentValue, setCurrentValue] = useState<[number, number]>(
    data?.active || data?.range || [0, 1000]
  )

  // Simple debounce for price filter
  const debounceRef = useRef<NodeJS.Timeout | null>(null)

  const debouncedPriceFilter = useCallback(
    (newRange: [number, number]) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
      debounceRef.current = setTimeout(() => {
        handleToggleFilter(`ss_price:${newRange[0]}<>${newRange[1]}`)
      }, 500)
    },
    [handleToggleFilter]
  )

  const handleRangeChange = useCallback(
    (newRange: [number, number]) => {
      setCurrentValue(newRange)
      debouncedPriceFilter(newRange)
    },
    [debouncedPriceFilter]
  )

  useEffect(() => {
    if (data?.field !== "ss_price" || !data?.range) return
    setStableRange(data?.range)
  }, [])

  useEffect(() => {
    if (data?.field !== "ss_price") return
    setCurrentValue(data?.active || data?.range || [0, 1000])
  }, [data?.active, data?.range])

  useEffect(() => {
    const details = detailsRef.current
    const toggleOpen = () => setIsOpen(details?.open)

    if (details) {
      details.addEventListener("toggle", toggleOpen)
    }

    return () => {
      if (details) {
        details.removeEventListener("toggle", toggleOpen)
      }
    }
  }, [])

  const values = useMemo(() => {
    // Size facet: relabel legacy numeric values to alpha and merge values that
    // resolve to the same label into one pill (#1747). A merged pill toggles as
    // a group: when checked, only the active member keys are toggled (all off);
    // when unchecked, all member keys are toggled (all on). Per-member Set
    // toggling would oscillate between members and never clear.
    if (data?.field === SIZE_FACET_FIELD) {
      const groups = new Map<
        string,
        { keys: string[]; activeKeys: string[]; count: number }
      >()

      data.values.forEach((item) => {
        const rawValue = String(item.value ?? item.label ?? "")
        const label = sizeLabel(rawValue)
        const key = `${data.field}:${rawValue}`
        const group = groups.get(label) || {
          keys: [],
          activeKeys: [],
          count: 0,
        }

        group.keys.push(key)
        if (item?.active) group.activeKeys.push(key)
        group.count += item?.count ?? 0
        groups.set(label, group)
      })

      return Array.from(groups.entries())
        .sort(([a], [b]) => sizeRank(a) - sizeRank(b))
        .map(([label, group]) => {
          const checked = group.activeKeys.length > 0
          return {
            label,
            value: (checked ? group.activeKeys : group.keys).join(","),
            count: group.count,
            checked,
            disabled: false,
          }
        })
    }

    return (
      data.values.map((item) => {
        const value = item.value || item.label
        const valueWithKey = `${data.field || item.field}:${value}`

        const fetchedValue = false

        return {
          label: String(item.label).toLowerCase(),
          value: isSortBy ? item.value : valueWithKey,
          count: item?.count,
          checked: item?.active || data?.activeSort === item?.label,
          disabled: !fetchedValue || false,
        }
      }) ?? []
    )
  }, [data, activeFilters])

  // Hide the entire price filter accordion if currency is not AUD
  if (isPrice && currentCountryCurrency?.currencyCode !== "AUD") {
    return null
  }

  return (
    <div className="product--accordion accordion">
      <details ref={detailsRef}>
        <summary className="accordion--title">
          {data?.label}
          <span className={`chevron-icon ${isOpen ? "open" : ""}`}>
            {chevronDown}
          </span>
        </summary>
        <div
          className={`${isSortBy ? "" : "collapsible__content"} accordion__content`}
        >
          {isPrice && (
            <div className="price-range-slider__container">
              <RangeSlider
                min={stableRange[0]}
                max={stableRange[1]}
                step={1}
                value={currentValue}
                onInput={handleRangeChange}
              />
              <div className="price-range-slider__prices">
                <p>${stableRange[0]}</p>
                <p>${stableRange[1]}</p>
              </div>
            </div>
          )}

          {values.map((value, index) => (
            <div
              key={index}
              className="filter-sort-options--item"
              onClick={
                isSortBy
                  ? () => refine("sort", value.value)
                  : () => handleToggleFilter(value.value)
              }
            >
              <input
                type={isSortBy ? "radio" : "checkbox"}
                className={isSortBy ? "custom-radio" : "custom-check"}
                checked={value.checked}
              />
              {value.label && (
                <p className="filter-sort-option--title">{value.label}</p>
              )}
              {value.count && (
                <p className="filter-sort-option--count">({value.count})</p>
              )}
            </div>
          ))}
        </div>
      </details>
    </div>
  )
}
